export const OPTIONAL_FIELDS_BY_TYPE: Record<string, string[]> = {
  NavMainLink: ['subMenu'],
  // Add more types as needed when parity tests reveal them
};

export const RICH_TEXT_ENTRY_LINK_KEYS: Record<string, Array<'inline' | 'block' | 'hyperlink'>> = {
  // Recorded fixture parity: HeroConsolidated.copy includes `hyperlink` and `inline` (no `block`)
  'HeroConsolidated.copy': ['inline', 'hyperlink'],
  'HeroConsolidated.disclaimers': ['inline', 'hyperlink'],
  'MediaCopy.copy': ['inline', 'hyperlink'],
  'HomepageHero.copy': ['inline', 'hyperlink'],
  'HomepageHero.headline': ['inline', 'hyperlink'],
  'HomepageHero.disclaimers': ['inline', 'hyperlink'],
  'HomepageHero.focusDisclaimers': ['inline', 'hyperlink'],
  'CardsIconItem.cardCopy': ['inline', 'hyperlink'],
  'CardsImageItem.cardCopy': ['inline', 'hyperlink'],
  'CardsImageItem.cardHeadlineRte': ['inline'],
  'ImpactHeadline.copy': ['inline', 'hyperlink'],
  'CalloutIconCard.copy': ['inline', 'hyperlink'],
  'CalloutGridCard.copy': ['inline', 'hyperlink'],
  'CalloutImageCard.copy': ['inline', 'hyperlink'],
  'Card.copy': ['inline', 'hyperlink'],
  'CalloutImageCard.headline': ['inline', 'hyperlink'],
  'Modal.copy': ['inline', 'hyperlink'],
  'Resource.resourceCopy': ['inline', 'hyperlink'],
  'ContentBlock.copy': ['inline', 'hyperlink'],
  'ContentBlock.headline': ['inline', 'hyperlink'],
  'CardsStackedItem.shortDescription': ['inline', 'hyperlink'],
  'TestimonialItem.copy': ['inline', 'hyperlink'],
  'BentoBoxCard.copy': ['inline', 'hyperlink'],
  'BentoBoxAppReviewCard.copy': ['inline', 'hyperlink'],
  'BentoBoxCardLarge.cardCopy': ['inline', 'hyperlink'],
  'BentoBoxCardLarge.copy': ['inline', 'hyperlink'],
  'BentoBox.copy': ['inline', 'hyperlink'],
  'ContentBlock.eyebrow': ['inline', 'hyperlink'],
  'MapLocator.copy': ['inline', 'hyperlink'],
  'Note.copy': ['inline', 'hyperlink'],
  'Note.headline': ['inline', 'hyperlink'],
  'Launchpad.copy': ['inline', 'hyperlink'],
  'Accordion.copy': ['inline', 'hyperlink'],
  'AccordionItem.accordionCopy': ['inline', 'hyperlink'],
  'AccordionItem.accordionLabelRte': ['inline'],
  'RichTextEditor.content': ['inline', 'hyperlink'],
  'TextEditor.content': ['inline', 'hyperlink'],
  'CardsVerticalScrollItem.headline': ['inline'],
  'CardsVerticalScrollItem.copy': ['inline', 'hyperlink'],
  'HubSpot.rightColumnContent': ['inline', 'block', 'hyperlink'],
};

export const FULL_SHORTCODE_INLINE_CONTEXT_KEYS = new Set([
  'HeroConsolidated.copy',
  'ContentBlock.copy',
  'ContentBlock.headline',
  'MediaCopy.copy',
  'MediaCopy.headline',
  'ImpactHeadline.copy',
  'CalloutIconCard.copy',
  'CalloutIconCard.headline',
  'CalloutIconCard.headlineSmallRte',
  'CardsIconItem.cardCopy',
  'CardsImageItem.cardHeadlineRte',
  'CardsGridComplexItem.headline',
  'CardsStackedItem.headline',
  'CardsStackedItem.shortDescription',
  'CardsExpandableItem.cardCopy',
  'BentoBoxCard.copy',
  'BentoBoxCard.cardHeadlineRte',
  'BentoBoxAppReviewCard.copy',
  'Compare.content',
  // Be permissive about unmapped keys for BentoBox card copy across fragments
  'BentoBoxCard.cardCopy',
  'BentoBoxAppReviewCard.cardCopy',
  'Card.headline',
  'AccordionItem.accordionCopy',
  'CalloutGridCard.headline',
  'CalloutGridCard.headlineSmallRte',
  'CalloutGridCard.copy',
  'Card.copy',
  'CardsVerticalScrollItem.headline',
  'CardsVerticalScrollItem.copy',
  'Disclosure.copy',
  'HomepageHero.copy',
  'RichTextEditor.content',
  'TextEditor.content',
  'HubSpot.rightColumnContent',
]);

export const RICH_TEXT_JSON_ONLY_KEYS = new Set([
  'Shortcode.shortCodeValue',
  'UnlockedText.unlockedText',
  'Compare.graphDescription',
  'CompareTableVariable.name',
]);

export const MINIMAL_HYPERLINK_ENTRY_CONTEXT_KEYS = new Set([
  'HeroConsolidated.copy',
  'Modal.copy',
  'HeroConsolidated.disclaimers',
  'HomepageHero.disclaimers',
  'HomepageHero.focusDisclaimers',
  'RichTextEditor.content',
  'TextEditor.content',
  'AccordionItem.accordionCopy',
  'AccordionItem.accordionLabelRte',
  'CalloutImageCard.headline',
  'ContentBlock.copy',
  'Card.copy',
  'CardsStackedItem.shortDescription',
]);

export const RICH_TEXT_ASSET_LINK_KEYS: Record<string, Array<'block'>> = {
  'ContentBlock.copy': ['block'],
  'ContentBlock.headline': ['block'],
  'ContentBlock.eyebrow': ['block'],
  'HomepageHero.headline': ['block'],
  'RichTextEditor.content': ['block'],
  'TextEditor.content': ['block'],
  'CardsIconItem.cardCopy': ['block'],
  'CalloutIconCard.copy': ['block'],
  'HubSpot.rightColumnContent': ['block'],
};

/**
 * Maps REST field names to GraphQL aliased field names.
 * This handles the aliasing done in GraphQL fragments.
 *
 * Format: 'restFieldName': 'graphqlAliasName'
 *
 * For type-specific mappings, use: 'TypeName.fieldName': 'aliasName'
 *
 * See libs/contentful/rest/__tests__/schema-parity.test.ts for full coverage of required mappings.
 */
export const FIELD_ALIAS_MAPPINGS: Record<string, string> = {
  // Nav fields - GraphQL uses aliases for these
  topNavBarSecondaryLink: 'topBarLink',
  topLevelLinks: 'navContent', // Array of NavContentLink entries
  'Nav.resources': 'resourcesCollection', // Nav specifically needs resourcesCollection
  'BlogNavigation.resources': 'resourcesCollection',

  // PageCareersTemplate uses different field names than the standard Page content type
  'PageCareersTemplate.navigation': 'nav',
  'PageCareersTemplate.disclosuresFooterTop': 'disclosures',
  'PageCareersTemplate.disclosuresFooterBottom': 'disclosuresBottom',
  // Header logo fields - note: REST field is 'items', maps to 'headerLogoItems'
  'HeaderLogo.items': 'headerLogoItems',
  // Hero carousel - REST field 'slides' should keep as 'slides'
  slides: 'slides',
  // Type-specific content mappings
  'CardsIconCarousel.content': 'cardIconItems',
  'CardsImageCarousel.content': 'cardImageItems',
  'Accordion.content': 'accordionItems',
  'Faq.content': 'toggleItems', // FAQ toggle items
  'ToggleItem.content': 'contentCollection', // Toggle item children
  'ImpactHeadline.cta': 'ctas', // Aliased to ctas in ImpactHeadline
  'SectionDisclosures.disclosures': 'disclosuresCollection', // Aliased to disclosuresCollection
  'DisclosureAddOn.disclosures': 'disclosuresCollection', // Aliased to disclosuresCollection
  'DisclosureCollection.groupings': 'groupingsCollection', // DisclosureCollection uses groupingsCollection
  // Card-related mappings
  cardBackgroundColor: 'color', // BentoBox and other card components
  cards: 'cards', // Already aliased in GraphQL, just needs wrapping

  // Mappings added from schema parity tests
  'BentoBoxCard.cardIcon': 'icon',
  'BentoBoxCard.cardCopy': 'copy',
  'BentoBoxCardLarge.cardCopy': 'copy',
  'CalloutGridCard.icon': 'iconName',
  'CardsStackedItem.icon': 'iconAsset',
  'ResourcesItgBranded.content': 'resources',
  'CardsIcon.content': 'cardIconItems',
  'CardsImage.content': 'cardImageItems',
  'CardsIconItem.hyperlinkedImages': 'hyperlinkedImagesCollection',
  'CardsImageItem.hyperlinkedImages': 'hyperlinkedImagesCollection',

  // MediaCopy field aliases
  'MediaCopy.hyperlinkedImages': 'hyperlinkedImagesCollection',
  'MediaCopy.sliderEntries': 'sliderEntryCollection',

  // HeroConsolidated field aliases
  'HeroConsolidated.hyperlinkedMedia': 'hyperlinkedMediaCollection',

  // Footer column links wrapping
  'FooterColumn.links': 'linksCollection',

  // Nav sub menu links wrapping
  'NavSubMenu.sublinks': 'sublinksCollection',

  // Subnav links wrapping
  'Subnav.links': 'linksCollection',

  // Teaser links wrapping
  'TeaserLinks.links': 'linksCollection',

  // CardsExpandable wrapping
  'CardsExpandable.content': 'cardsExpandableItems',
  'CardsExpandable.iconList': 'cardListItems',
  'CardsExpandableItem.iconList': 'cardListItems',

  // ArticleGrid wrapping
  'ArticleGrid.articles': 'articlesCollection',
  // CardsVerticalScroll: REST field is 'cardsVerticalScrollItem', GraphQL aliases to 'cards'
  'CardsVerticalScroll.cardsVerticalScrollItem': 'cards',

  // SectionBanner: REST field is 'headerBanner', needs to be mapped to 'headerBannerCollection'
  'SectionBanner.headerBanner': 'headerBannerCollection',

  // Agreement page: REST field names → collection names
  'PageAgreement.sections': 'sectionsCollection',
};

/**
 * Gets the mapped field name if an alias exists, otherwise returns original name.
 * Supports type-specific mappings in format: 'TypeName.fieldName'
 */
export function getMappedFieldName(fieldName: string, typename?: string): string {
  // Try type-specific mapping first
  if (typename) {
    const typeSpecificKey = `${typename}.${fieldName}`;
    if (FIELD_ALIAS_MAPPINGS[typeSpecificKey]) {
      return FIELD_ALIAS_MAPPINGS[typeSpecificKey];
    }
  }
  // Fall back to generic mapping
  return FIELD_ALIAS_MAPPINGS[fieldName] ?? fieldName;
}

/**
 * For array fields that contain entries, determines the output field name
 * and whether it should be wrapped in {items: [...]}.
 *
 * Returns the mapped name (without adding Collection suffix since
 * the items wrapper replaces the Collection naming convention).
 */
export function getCollectionFieldName(fieldName: string, typename?: string): string {
  // Get the mapped field name (type-specific if typename provided)
  return getMappedFieldName(fieldName, typename);
}

/**
 * Content types that are treated as "simple data containers" by GraphQL.
 * These types don't have __typename in GraphQL output - they're just data objects.
 */
export const SIMPLE_DATA_CONTAINER_TYPES = [
  'author',
  'bentoBoxCardLarge',
  'customQueryStringParameter',
  'disclosureItem',
  'disclosureSymbol',
  'accordionItem',
  'aspectRatio',
  'badgeOverlay',
  'calloutImageCard',
  'calloutIconCard',
  'cardsGridSimpleItem',
  'eyebrow',
  'footerColumn',
  'headerLogoItem',
  'hyperlinkedMedia',
  'iconBullet',
  'modal',
  'navMainLink',
  'navSubMenu',
  'navSubLink',
  'notificationOverlay',
  'responsiveMedia',
  'supportedAspectRatios',
  'teaserLinkItem',
  'socialMediaLink',
  'subnavLink',
  'toggleItem',
  'agreementSection',
  'agreementSectionBlock',
];

export const ASSET_FIELDS_WITHOUT_SYS = new Set([
  // Recorded fixture parity: these media fields are queried without sys
  'desktopMedia',
  'mobileMedia',
  'desktopImage',
  'mobileImage',
  'cardImage',
  'iconImage',
  'media',
  'resourceIcon',
  'icon',
  'eyebrowAlternateLogo',
  'eyebrowChimeLogo',
  'seoOpenGraphImage',
  'previewImage',
  'imageOverlay',
  'backgroundImage',
]);
